import { z } from "zod";
import {
  MAX_DATED_EXCEPTIONS,
  MAX_WEEKLY_SHIFTS,
  CalendarExceptionSchema,
  WeeklyShiftSchema,
  checkCalendarInvariants,
} from "./operating-calendar.js";
import { RecurringExceptionSchema, RecurringExceptionsSchema } from "./recurring-exception.js";

// ── A organização como superfície editável ────────────────────────────────────────────────────
// Duas coisas nascem aqui. A primeira é o CALENDÁRIO DA FÁBRICA: feriado é fato da organização, não
// da linha, e até agora declarar Natal significava repetir a mesma exceção em cada linha sem nada
// mantendo-as sincronizadas. A segunda é mais simples e mais constrangedora: o fuso da fábrica
// nunca teve superfície nenhuma — era editável só por banco, apesar de decidir em que hora civil
// todo turno declarado é resolvido.

// ── Identidade ────────────────────────────────────────────────────────────────────────────────

/**
 * Fuso IANA. Validado pelo runtime em vez de por lista: `Intl.DateTimeFormat` conhece a base de
 * dados de fusos do próprio ambiente que vai resolver os turnos, então uma lista literal só poderia
 * discordar dele — e discordaria para pior, envelhecendo a cada revisão da IANA.
 */
export const TimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine(
    (tz) => {
      try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: "Fuso horário inválido — use um identificador IANA (ex.: America/Fortaleza)" }
  );

// ── Política de julgamento — a RÉGUA DO GÊMEO (Spec 079) ──────────────────────────────────────
//
// Três números que decidem o que a tela AFIRMA sobre o modelo. São configuração da organização,
// nunca constante de código (FR-030): sob a alternativa, mudar a amostra mínima de 12 para 20
// exigiria PR, revisão e deploy — exatamente o caminho que o fuso da fábrica percorria antes desta
// mesma tela existir.
//
// ⚠️ Percentual 0..100, alinhado a `oee-thresholds.ts` e ao `Decimal(5,2)` que a plataforma usa
// para todo percentual. Fração 0..1 aqui seria a única exceção de unidade do produto, e exceção de
// unidade é como nasce erro de duas ordens de grandeza.

const POLICY_SHAPE = {
  /**
   * A partir de quantos compromissos TESTADOS uma régua vale (FR-014).
   *
   * Padrão 12, e não 5: enquanto o histórico apenas rebaixava a confiança, amostra pequena era
   * conservadora; substituindo a procedência (Spec 079), amostra pequena é temerária.
   */
  trackRecordMinSample: z.number().int().min(1).max(10_000),
  /** Acurácia a partir da qual o nível exibido é `HIGH`. */
  trackRecordAccuracyHigh: z.number().min(0).max(100),
  /** A partir da qual é `MEDIUM`; abaixo, `LOW`. */
  trackRecordAccuracyMedium: z.number().min(0).max(100),

  // ── Os SETE resgatados de código e de ambiente (R11) ────────────────────────────────────────
  //
  // Já decidiam o produto antes desta spec, escondidos em constante de módulo e em variável de
  // ambiente. Entram na MESMA rota e no MESMO formulário que os três acima porque são a mesma
  // categoria de coisa: número que decide o que a tela AFIRMA, e que só o dono do negócio sabe
  // calibrar. Agrupá-los por onde MORAVAM (código × env × banco) seria organizar a tela pela
  // história da implementação em vez de pela decisão que cada um toma.

  /** Verde do OEE (0..100); abaixo do próximo, amarelo. Era o literal `85`, em duas cópias. */
  oeeThresholdGood: z.number().min(0).max(100),
  /** Amarelo do OEE (0..100); abaixo, vermelho. Era o literal `65`. */
  oeeThresholdWarn: z.number().min(0).max(100),
  /**
   * Piso da faixa "no plano". Abaixo dela, ATRASADO. Era o literal `95`.
   *
   * ⚠️ Teto 200 e não 100: aderência ao plano NÃO é capada (o overrun é sinal legítimo), e uma
   * fábrica pode legitimamente considerar "no plano" uma faixa que começa acima de 100.
   */
  planAdherenceLow: z.number().min(0).max(200),
  /** Teto da faixa "no plano". Acima dele, ADIANTADO. Era o literal `105`. */
  planAdherenceHigh: z.number().min(0).max(200),
  /**
   * Carência entre o fim da vigência e a liberação da apuração, em horas.
   *
   * Era `SETTLEMENT_GRACE_HOURS`, replicada em DOIS serviços que precisavam afirmar o mesmo valor
   * no boot só para que a divergência fosse descobrível lendo dois logs lado a lado. Com um valor
   * só no banco, divergir deixa de ser possível — o problema some em vez de ficar monitorado.
   */
  settlementGraceHours: z.number().int().min(0).max(8_760),
  /** Fração de máquinas sem dado (0..100) a partir da qual a confiança do OEE é `LOW`. Era `0.1`. */
  oeeConfidenceNodataHigh: z.number().min(0).max(100),
  /** ... e acima da qual é `MEDIUM`. Era `0.02`. PERCENTUAL, como todo percentual do produto. */
  oeeConfidenceNodataLow: z.number().min(0).max(100),
} as const;

/**
 * Os MESMOS padrões que as colunas declaram (`@default(...)` em `schema.prisma`).
 *
 * ⚠️ NÃO é a política vigente — a política vive no banco e é editável (FR-030). Isto existe só para
 * os caminhos em que a linha da organização não pôde ser lida, e mora aqui, ao lado do schema, para
 * que o número não passe a existir em duas versões: o `?? 0` que estava em `toIdentity` produzia
 * `minSample: 0` (fora do próprio `min(1)` acima) e `alta = média = 0`, que faz TODA acurácia virar
 * `HIGH`. Zero não é um padrão conservador aqui; é o pior valor possível.
 *
 * ⚠️ Os sete de baixo repetem os literais que a Spec 079 REMOVEU do código (`85`/`65` das cores,
 * `95`/`105` das faixas, `24` da env, `0.1`/`0.02` das fatias de NODATA — estes dois em percentual).
 * Repetir o número aqui NÃO reintroduz a constante proibida: o valor que o produto usa é sempre o
 * da organização, e isto é o piso para quando a leitura falha.
 */
export const DEFAULT_ORGANIZATION_POLICY = {
  trackRecordMinSample: 12,
  trackRecordAccuracyHigh: 85,
  trackRecordAccuracyMedium: 60,
  oeeThresholdGood: 85,
  oeeThresholdWarn: 65,
  planAdherenceLow: 95,
  planAdherenceHigh: 105,
  settlementGraceHours: 24,
  oeeConfidenceNodataHigh: 10,
  oeeConfidenceNodataLow: 2,
} as const;

/** O recorte da régua. Alias mantido porque `track-record-resolve.ts` só precisa dos três. */
export const DEFAULT_TRACK_RECORD_POLICY = DEFAULT_ORGANIZATION_POLICY;

export type OrganizationPolicy = {
  -readonly [K in keyof typeof DEFAULT_ORGANIZATION_POLICY]: number;
};

/**
 * OS QUATRO PARES ORDENADOS (FR-032), valendo no formulário e na rota.
 *
 * Um par invertido não produz erro nenhum — produz uma partição VAZIA no meio, e o mapa
 * valor → faixa passa a mentir em silêncio: nenhuma acurácia seria `MEDIUM`, nenhum OEE seria
 * amarelo, nenhuma run estaria "no plano". A faixa some da tela sem nada dizendo por quê, e o
 * chamado que chega meses depois é "o amarelo do painel parou de funcionar".
 *
 * ⚠️ Cada checagem só compara quando os DOIS lados vieram. Um PATCH que altera um lado só não tem
 * contra o quê comparar aqui — quem sabe o valor vigente do outro é o banco, e a checagem completa
 * vive no service. A tela envia os dez juntos, de propósito (um formulário, um salvar).
 *
 * ⚠️ O `path` ancora no campo MAIOR do par, sempre. Ancorar num campo ausente faria a mensagem não
 * aparecer sob nenhum input do formulário — o erro existiria e ninguém o veria.
 */
const ORDERED_PAIRS = [
  {
    high: "trackRecordAccuracyHigh",
    low: "trackRecordAccuracyMedium",
    message: "A acurácia de confiança alta deve ser maior que a de confiança média",
  },
  {
    high: "oeeThresholdGood",
    low: "oeeThresholdWarn",
    message: "O limiar verde do OEE deve ser maior que o amarelo",
  },
  {
    high: "planAdherenceHigh",
    low: "planAdherenceLow",
    message: "O teto da faixa 'no plano' deve ser maior que o piso",
  },
  {
    high: "oeeConfidenceNodataHigh",
    low: "oeeConfidenceNodataLow",
    message:
      "A fatia que rebaixa a confiança para baixa deve ser maior que a que a rebaixa para média",
  },
] as const;

const checkOrderedPairs = (b: Record<string, unknown>, ctx: z.RefinementCtx): void => {
  for (const pair of ORDERED_PAIRS) {
    const high = b[pair.high];
    const low = b[pair.low];
    if (typeof high !== "number" || typeof low !== "number") continue;
    if (high > low) continue;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [pair.high],
      message: pair.message,
    });
  }
};

/**
 * O que `GET /organization` devolve. Chama-se `Identity` e não `Organization` porque a entidade
 * `Organization` já existe em `entities/` com o registro inteiro (incluindo `totpRequired`,
 * `deletedAt`): este é o recorte que a tela edita, e confundir os dois nomes é como um campo
 * sensível vaza para uma resposta que não deveria carregá-lo.
 */
export const OrganizationIdentitySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  timezone: TimezoneSchema,
  ...POLICY_SHAPE,
});
export type OrganizationIdentity = z.infer<typeof OrganizationIdentitySchema>;

export const OrganizationEnvelopeSchema = z.object({
  data: OrganizationIdentitySchema,
  error: z.null(),
});
export type OrganizationEnvelope = z.infer<typeof OrganizationEnvelopeSchema>;

/**
 * O corpo do PATCH, antes dos refinamentos.
 *
 * Objeto NOMEADO porque `.refine`/`.superRefine` devolvem `ZodEffects`, que não tem `.extend()` —
 * e esta superfície ainda vai crescer (os outros sete parâmetros de política). Sem o objeto base,
 * o próximo campo obrigaria a desmontar a validação para acrescentá-lo.
 */
const UpdateOrganizationShape = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  timezone: TimezoneSchema.optional(),
  trackRecordMinSample: POLICY_SHAPE.trackRecordMinSample.optional(),
  trackRecordAccuracyHigh: POLICY_SHAPE.trackRecordAccuracyHigh.optional(),
  trackRecordAccuracyMedium: POLICY_SHAPE.trackRecordAccuracyMedium.optional(),
  oeeThresholdGood: POLICY_SHAPE.oeeThresholdGood.optional(),
  oeeThresholdWarn: POLICY_SHAPE.oeeThresholdWarn.optional(),
  planAdherenceLow: POLICY_SHAPE.planAdherenceLow.optional(),
  planAdherenceHigh: POLICY_SHAPE.planAdherenceHigh.optional(),
  settlementGraceHours: POLICY_SHAPE.settlementGraceHours.optional(),
  oeeConfidenceNodataHigh: POLICY_SHAPE.oeeConfidenceNodataHigh.optional(),
  oeeConfidenceNodataLow: POLICY_SHAPE.oeeConfidenceNodataLow.optional(),
});

export const UpdateOrganizationRequestSchema = UpdateOrganizationShape.superRefine((b, ctx) => {
  if (Object.values(b).every((v) => v === undefined)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Informe ao menos um campo para alterar",
    });
  }
  checkOrderedPairs(b, ctx);
});
export type UpdateOrganizationRequest = z.infer<typeof UpdateOrganizationRequestSchema>;

// ── Calendário da organização ─────────────────────────────────────────────────────────────────

/**
 * O expediente padrão da fábrica. Mesma forma do calendário da linha MENOS `shiftsMode`: a
 * organização é a camada base, não tem de quem herdar.
 */
export const OrganizationCalendarSchema = z
  .object({
    weeklyShifts: z.array(WeeklyShiftSchema).max(MAX_WEEKLY_SHIFTS),
    exceptions: z.array(CalendarExceptionSchema).max(MAX_DATED_EXCEPTIONS),
    recurringExceptions: RecurringExceptionsSchema.default([]),
  })
  .superRefine(checkCalendarInvariants);
export type OrganizationCalendar = z.infer<typeof OrganizationCalendarSchema>;
export type OrganizationCalendarInput = z.input<typeof OrganizationCalendarSchema>;

export const UpsertOrganizationCalendarRequestSchema = OrganizationCalendarSchema;
export type UpsertOrganizationCalendarRequest = z.infer<
  typeof UpsertOrganizationCalendarRequestSchema
>;

export const OrganizationCalendarResponseSchema = z.object({
  /** `null` quando a organização ainda não declarou expediente — o estado vazio da tela. */
  calendar: OrganizationCalendarSchema.nullable(),
  /**
   * Quantas linhas seguem o padrão da fábrica.
   *
   * É CONTAGEM, não projeção: a spec proíbe calcular ou exibir o deslocamento que a mudança causará
   * nos indicadores. Prometer "o OEE da linha X vai cair 4 pp" exigiria recalcular o histórico
   * inteiro contra um calendário hipotético, e o número sairia errado sempre que a janela consultada
   * na tela não fosse a mesma da projeção.
   *
   * Vive no GET do calendário e não em endpoint próprio (research): é contexto da tela, não
   * recurso — a tela sempre o pede junto.
   */
  inheritingLineCount: z.number().int().nonnegative(),
});
export type OrganizationCalendarResponse = z.infer<typeof OrganizationCalendarResponseSchema>;

export const OrganizationCalendarEnvelopeSchema = z.object({
  data: OrganizationCalendarResponseSchema,
  error: z.null(),
});
export type OrganizationCalendarEnvelope = z.infer<typeof OrganizationCalendarEnvelopeSchema>;

// ── Catálogo de feriados nacionais ────────────────────────────────────────────────────────────

/** Faixa dos anos aceitos. Contém digitação absurda; não modela horizonte de uso. */
export const HOLIDAY_YEAR_MIN = 2020;
export const HOLIDAY_YEAR_MAX = 2100;

export const HolidaysQuerySchema = z.object({
  year: z.coerce.number().int().min(HOLIDAY_YEAR_MIN).max(HOLIDAY_YEAR_MAX),
});
export type HolidaysQuery = z.infer<typeof HolidaysQuerySchema>;

export const HolidaySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  name: z.string(),
  /** Data móvel (derivada da Páscoa) — é onde um erro de offset se esconde por um ano inteiro. */
  movable: z.boolean(),
  /**
   * `true` para o que NÃO é feriado nacional mas é dia parado na maioria das plantas (a segunda de
   * Carnaval). Vem desmarcado por padrão: sugerir é útil, decidir pelo gestor não é.
   */
  suggestion: z.boolean().default(false),
});
export type Holiday = z.infer<typeof HolidaySchema>;

export const HolidaysResponseSchema = z.object({
  year: z.number().int(),
  holidays: z.array(HolidaySchema),
});
export type HolidaysResponse = z.infer<typeof HolidaysResponseSchema>;

export const HolidaysEnvelopeSchema = z.object({
  data: HolidaysResponseSchema,
  error: z.null(),
});
export type HolidaysEnvelope = z.infer<typeof HolidaysEnvelopeSchema>;

// ── Carregamento do catálogo no calendário ────────────────────────────────────────────────────

/**
 * `POST /organization/calendar/holidays` — funde os feriados escolhidos no calendário da fábrica.
 *
 * ADIÇÃO AO CONTRATO (`http-delta.md` previa só o GET do catálogo). A razão é que a regra é uma
 * garantia de ESCRITA — "data já presente não duplica, motivo editado à mão não é sobrescrito" —, e
 * garantia de escrita implementada só na tela vale só para a tela. Qualquer outro caminho (o
 * assistente, um import, um segundo cliente) reintroduziria o duplicado.
 *
 * A alternativa era a interface montar o merge e mandar o calendário inteiro por `PUT`. Funciona no
 * caso feliz e falha no que importa: entre o `GET` que a tela usou de base e o `PUT`, o calendário
 * pode ter mudado — e o `PUT` substitui integralmente, então a tela apagaria o que não viu.
 */
export const LoadHolidaysRequestSchema = z.object({
  /** Só os que o gestor MANTEVE marcados na revisão. A tela decide o quê; o servidor, o como. */
  holidays: z.array(HolidaySchema).max(60),
});
export type LoadHolidaysRequest = z.infer<typeof LoadHolidaysRequestSchema>;
export type LoadHolidaysRequestInput = z.input<typeof LoadHolidaysRequestSchema>;

// ── Prévia de ocorrências de uma regra recorrente ─────────────────────────────────────────────

export const OCCURRENCES_LIMIT_DEFAULT = 6;

/**
 * Read-model EFÊMERO: não cria nada, não audita, não invalida cache — o irmão de
 * `POST /scenarios/:id/preview`.
 *
 * Existe porque a interface NUNCA interpreta regra de calendário. Se o frontend
 * calculasse "a primeira segunda de setembro", passaria a existir uma TERCEIRA implementação da
 * expansão de recorrência, e ela seria a única que o gestor vê antes de salvar — divergir dela é
 * divergir exatamente no ponto onde a confiança se forma.
 */
export const PreviewOccurrencesRequestSchema = z.object({
  rule: RecurringExceptionSchema,
  limit: z.number().int().min(1).max(24).default(OCCURRENCES_LIMIT_DEFAULT),
  /** Âncora da busca. Ausente ⇒ o servidor usa "agora" no fuso da organização. */
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type PreviewOccurrencesRequest = z.infer<typeof PreviewOccurrencesRequestSchema>;
export type PreviewOccurrencesRequestInput = z.input<typeof PreviewOccurrencesRequestSchema>;

export const PreviewOccurrencesResponseSchema = z.object({
  occurrences: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
});
export type PreviewOccurrencesResponse = z.infer<typeof PreviewOccurrencesResponseSchema>;

export const PreviewOccurrencesEnvelopeSchema = z.object({
  data: PreviewOccurrencesResponseSchema,
  error: z.null(),
});
export type PreviewOccurrencesEnvelope = z.infer<typeof PreviewOccurrencesEnvelopeSchema>;
